/**
 * PatientQuickView — modal preview of a patient row from the clinician
 * dashboard. Loads /api/patients/{id} on open and shows the essentials
 * (contact, demographics, goals, next appointment) with a link to the
 * full-page profile.
 *
 * Kept intentionally read-only — edits happen on /clinician/patient/{id}.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ArrowUpRight, CalendarClock, FilePlus2, Loader2, Mail, MapPin,
  MessageSquare, Phone, Target, User, X,
} from "lucide-react";
import api from "@/lib/api";

export default function PatientQuickView({ patientId, onClose }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr(""); setP(null);
      try {
        const { data } = await api.get(`/patients/${patientId}`);
        if (!cancelled) setP(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="patient-quickview"
    >
      <div
        className="fl-card fl-card-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 md:p-7 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-zinc-500 hover:text-white"
          aria-label="Close"
          data-testid="patient-quickview-close"
        >
          <X size={18} />
        </button>

        {!p && !err && (
          <div className="flex items-center gap-3 text-sm text-zinc-400">
            <Loader2 size={16} className="animate-spin" /> Loading patient…
          </div>
        )}
        {err && (
          <div className="text-sm text-red-400" data-testid="patient-quickview-error">
            Could not load patient: {String(err).slice(0, 200)}
          </div>
        )}

        {p && (
          <>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full bg-[#f47a20]/15 border border-[#f47a20]/40 flex items-center justify-center shrink-0">
                <User size={20} className="text-[#f47a20]" />
              </div>
              <div className="min-w-0">
                <div className="fl-heading text-2xl leading-tight truncate" data-testid="patient-quickview-name">
                  {p.name || p.email}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Patient · joined {p.created_at ? new Date(p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                </div>
                {p.assigned_clinician_name && (
                  <div className="text-[11px] text-zinc-400 mt-1">
                    Under care of <span className="text-zinc-200">{p.assigned_clinician_name}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="fl-label text-[#f47a20] mt-6 mb-2">Contact</div>
            <div className="grid gap-2 text-sm">
              <Row icon={Mail} label="Email" value={p.email} testId="pv-email"
                   href={p.email ? `mailto:${p.email}` : undefined} />
              <Row icon={Phone} label="Phone" value={p.phone}   testId="pv-phone"
                   href={p.phone ? `tel:${p.phone}` : undefined} />
              <Row icon={MapPin} label="Location" value={[p.city, p.country].filter(Boolean).join(", ")} testId="pv-location" />
            </div>

            <div className="fl-label text-[#f47a20] mt-6 mb-2">Profile</div>
            <div className="grid gap-2 text-sm">
              <Row icon={Activity} label="Sport"       value={p.sport}      testId="pv-sport" />
              <Row icon={Target}   label="Main goal"   value={p.main_goal}  testId="pv-goal" />
              <Row icon={User}     label="Preferred therapist"
                   value={p.preferred_therapist_name || p.assigned_clinician_name}
                   testId="pv-therapist" />
            </div>

            {p.reason_for_appointment && (
              <>
                <div className="fl-label text-[#f47a20] mt-6 mb-2">Reason for appointment</div>
                <div className="text-xs text-zinc-400 leading-relaxed whitespace-pre-line" data-testid="pv-reason">
                  {p.reason_for_appointment}
                </div>
              </>
            )}

            {(p.notes || p.medical_history) && (
              <>
                <div className="fl-label text-[#f47a20] mt-6 mb-2">Assessments &amp; Notes</div>
                <div className="text-xs text-zinc-400 leading-relaxed whitespace-pre-line" data-testid="pv-notes">
                  {p.medical_history || p.notes}
                </div>
              </>
            )}

            <div className="mt-6 flex flex-wrap gap-2 pt-4 border-t border-white/5">
              <Link
                to={`/clinician/patient/${p.id}`}
                onClick={onClose}
                className="fl-btn text-sm inline-flex items-center gap-2"
                data-testid="patient-quickview-open-profile"
              >
                Open full profile <ArrowUpRight size={13} />
              </Link>
              <Link
                to={`/clinician/notes?patient=${p.id}&walkin=1`}
                onClick={onClose}
                className="fl-btn-ghost text-sm inline-flex items-center gap-2"
                data-testid="pv-cta-walkin-note"
              >
                <FilePlus2 size={13} /> Walk-in note
              </Link>
              {p.email && (
                <a href={`mailto:${p.email}`} className="fl-btn-ghost text-sm inline-flex items-center gap-2" data-testid="pv-cta-email">
                  <Mail size={13} /> Email
                </a>
              )}
              {p.phone && (
                <a href={`tel:${p.phone}`} className="fl-btn-ghost text-sm inline-flex items-center gap-2" data-testid="pv-cta-call">
                  <Phone size={13} /> Call
                </a>
              )}
              <Link
                to={`/clinician/patient/${p.id}?tab=messages`}
                onClick={onClose}
                className="fl-btn-ghost text-sm inline-flex items-center gap-2"
                data-testid="pv-cta-message"
              >
                <MessageSquare size={13} /> Message
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, href, testId }) {
  if (!value) {
    return (
      <div className="flex items-center gap-2 text-zinc-500" data-testid={testId}>
        <Icon size={14} className="shrink-0" />
        <span className="text-xs uppercase tracking-widest">{label}</span>
        <span className="ml-auto text-zinc-600 text-xs">Not provided</span>
      </div>
    );
  }
  const inner = (
    <>
      <Icon size={14} className="text-[#f47a20] shrink-0" />
      <span className="text-xs uppercase tracking-widest text-zinc-400">{label}</span>
      <span className="ml-auto text-zinc-100 truncate max-w-[60%]" title={String(value)}>{value}</span>
    </>
  );
  return href ? (
    <a href={href} className="flex items-center gap-2 hover:text-[#f47a20] transition-colors" data-testid={testId}>
      {inner}
    </a>
  ) : (
    <div className="flex items-center gap-2" data-testid={testId}>{inner}</div>
  );
}
